<template>
  <Container>
    <Breadcrumbs class="my-4 lg:ml-4" />
    <div class="mb-10 gap-10 lg:grid lg:grid-cols-2">
      <div>
        <ShopProductGallery :product="product" />
      </div>
      <div>
        <h2
          class="text-md font-semibold uppercase tracking-wider max-md:mb-4 max-md:mt-8"
        >
          {{ getTranslatedProperty(product.manufacturer, 'name') }}
        </h2>

        <h1 class="mb-4 text-2xl font-bold lg:text-3xl">
          {{ getTranslatedProperty(product, 'name') }}
        </h1>

        <div class="mb-4 text-xs font-semibold text-gray-300">
          {{ $t('product.productNumber') }}:
          {{ product.productNumber }}
        </div>

        <ShopProductTags :tags="tags" />

        <div class="mt-8 flex gap-8 max-md:flex-col lg:flex-wrap">
          <ShopProductCharacteristic
            v-if="product.purchaseUnit"
            :label="$t('product.purchaseUnit')"
            :value="`${product.purchaseUnit} ${product.unit?.shortCode}`"
          />
          <ShopProductVariantConfigurator />
        </div>

        <div v-if="otherOfferText" class="mt-8">
          <span class="mb-2 text-xs font-medium uppercase">
            {{ $t('product.OtherOfferTextLabel') }}
          </span>
          <Bard
            class="mb-6 mt-2 w-full bg-gray-10 p-5 lg:w-auto"
            :content="otherOfferText"
          />
        </div>

        <ShopProductBuyBox :product="product" class="mt-8" />

        <div class="mt-8 flex flex-col">
          <UiAccordionItem
            :title="$t('product.description')"
            :content="getTranslatedProperty(product, 'description')"
            title-tag="h2"
            open
          />
          <UiAccordionItem
            v-if="product.customFields?.novu_jp_custom_products_more_info"
            :title="$t('product.moreDetails')"
            :content="product.customFields.novu_jp_custom_products_more_info"
            title-tag="h3"
          />
        </div>
      </div>
    </div>

    <ShopProductCrossSelling
      :cross-selling="productAssociations"
      class="mb-10"
    />
  </Container>
</template>

<script setup lang="ts">
import {
  getCategoryBreadcrumbs,
  getTranslatedProperty,
} from '@shopware/helpers'
import { useProductSearch } from '#imports'
import type { Tag } from '~/components/shop/product/Tags.vue'

const props = defineProps<{
  navigationId: string
}>()

const { search } = useProductSearch()
const { getCategoryRoute, getProductRoute } = useShopRouter()

const { data: productResponse } = await useAsyncData(
  'product' + props.navigationId,
  async () => {
    return await search(props.navigationId, {
      withCmsAssociations: false,
      criteria: {
        associations: {
          seoUrls: {},
          media: {},
          categories: {
            associations: {
              seoUrls: {},
            },
          },
          manufacturer: {
            associations: {
              media: {},
            },
          },
          crossSellings: {},
        },
      },
    })
  }
)

if (!productResponse.value) {
  throw createError({
    statusCode: 404,
    fatal: true,
    message: 'Product not found',
  })
}

// The detail route resolves a parent product id to one of its variants, so a
// parent URL would serve the variant's content under its own URL (duplicate
// content, JPR-628). Permanently redirect to the variant's URL instead, so
// only variant URLs stay reachable and indexable.
if (productResponse.value.product.id !== props.navigationId) {
  const variantRoute = getProductRoute(productResponse.value.product)
  if (variantRoute) {
    await navigateTo(variantRoute, { replace: true, redirectCode: 301 })
  }
}

const { product } = useProduct(
  productResponse.value.product,
  productResponse.value.configurator
)

const tags = computed(() => {
  const tags: Tag[] = []
  if (product.value.categories?.length) {
    product.value.categories.forEach((category) => {
      tags.push({
        name: category.name,
        link: getCategoryRoute(category),
      })
    })
  }
  if (product.value.calculatedPrice?.listPrice) {
    tags.push({
      name: '%',
      type: 'red',
    })
  }
  return tags
})

const categoryBreadcrumbs = getCategoryBreadcrumbs(product.value.seoCategory, {
  startIndex: 3,
})

const { data: productAssociations } = await useAsyncData(
  'productAssociations' + props.navigationId,
  async () => {
    const { loadAssociations, productAssociations } =
      useProductAssociations(product)

    await loadAssociations()
    return productAssociations.value
  }
)

const otherOfferText = computed(() => {
  if (product.value?.customFields?.novu_jp_custom_products_other_offer) {
    return product.value.customFields.novu_jp_custom_products_other_offer
  }
})

useBreadcrumbs([
  ...categoryBreadcrumbs,
  {
    name: getTranslatedProperty(product.value, 'name'),
  },
])

useProductJsonLD(product.value)

const metaLinks = useMetaLinks(product)

useHead({
  link: metaLinks,
})

useSeoMeta({
  title:
    getTranslatedProperty(product.value, 'metaTitle') ||
    getTranslatedProperty(product.value, 'name').substring(0, 60),
  description:
    getTranslatedProperty(product.value, 'metaDescription') ||
    getTranslatedProperty(product.value, 'description').substring(0, 160),
  ogTitle:
    getTranslatedProperty(product.value, 'metaTitle') ||
    getTranslatedProperty(product.value, 'name'),
  ogDescription:
    getTranslatedProperty(product.value, 'metaDescription') ||
    getTranslatedProperty(product.value, 'description'),
  keywords: getTranslatedProperty(product.value, 'keywords'),
})

if (product.value.cover?.media) {
  useSeoMeta({
    ogImageUrl: product.value.cover.media?.url,
  })
}
</script>
